import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSellerAuth } from '../../contexts/SellerAuthContext';
import useSEO from '../../hooks/useSEO';
import { useMsg91Otp, isMsg91Configured } from '../../components/Msg91Otp';
import '../seller/SellerAuth.css';

const SellerLogin = () => {
  useSEO({
    title: 'Seller Login – Buy Sell Taxi',
    description: 'Login to your Buy Sell Taxi seller account to manage your commercial vehicle listings.',
    url: 'https://www.buyselltaxi.com/seller/login',
    noindex: true,
  });
  const { login, loginWithAccessToken } = useSellerAuth();
  const { launch, ready, loading: widgetLoading, failed: widgetFailed } = useMsg91Otp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP sign-in sits alongside email/password rather than replacing it —
  // existing sellers have listings tied to accounts they log into with a
  // password, and a phone number they may no longer use.
  const handleOtpLogin = () => {
    setError('');
    launch({
      onSuccess: async (accessToken) => {
        setLoading(true);
        try {
          await loginWithAccessToken(accessToken);
          // A brand-new OTP seller lands on the dashboard with a placeholder
          // name and no email until they fill their profile in.
          navigate('/seller/dashboard');
        } catch (err) {
          setError(
            err.response?.status === 503
              ? 'OTP sign-in is unavailable right now. Use your email and password below.'
              : err.response?.data?.error || 'Could not sign you in. Please try again.'
          );
        } finally {
          setLoading(false);
        }
      },
      onFailure: () => setError(''),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/seller/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="seller-auth-page" data-testid="seller-login-page">
      <div className="seller-auth-card">
        <div className="seller-auth-logo">🚖</div>
        <h1>Buy Sell Taxi</h1>
        <p className="seller-auth-subtitle">Seller Portal</p>

        {error && <div className="message message-error">{error}</div>}

        {isMsg91Configured() && !widgetFailed && (
          <>
            <button
              type="button"
              className="btn-primary full-width"
              onClick={handleOtpLogin}
              disabled={!ready || widgetLoading || loading}
              data-testid="seller-otp-btn"
            >
              {widgetLoading ? 'Preparing sign-in…' : 'Sign in with OTP'}
            </button>
            <div className="admin-login-divider"><span>or use your password</span></div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="seller-login-email" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required data-testid="seller-login-password" />
          </div>
          <button type="submit" className="btn-primary full-width" disabled={loading} data-testid="seller-login-submit">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="auth-link">
          Don't have an account? <Link to="/seller/register" data-testid="register-link">Register here</Link>
        </p>

        <div className="admin-login-divider">
          <span>or</span>
        </div>
        <Link to="/admin/login" className="btn-admin-login" data-testid="admin-login-link">
          🔐 Admin Login
        </Link>
      </div>
    </div>
  );
};

export default SellerLogin;
